import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Truck,
  FileSpreadsheet,
  DollarSign,
  PlusCircle,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  TrendingUp,
  Calendar,
  Building2,
  PhoneCall,
  ShieldCheck,
  ChevronRight
} from 'lucide-react';
import { useAreaCliente } from '../../contexts/AreaClienteContext';
import { areaClienteService } from '../../services/areaClienteService';
import { OrdemDeServico } from '../../types/ordemServico';
import { Conta } from '../../types/financeiro';

export function AreaClienteDashboard() {
  const { clienteAtual, stats, refreshData } = useAreaCliente();
  const [recentOS, setRecentOS] = useState<OrdemDeServico[]>([]);
  const [recentContas, setRecentContas] = useState<Conta[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (!clienteAtual) return;
    let isMounted = true;
    setLoadingData(true);

    Promise.all([
      areaClienteService.getOrdensServicoCliente(clienteAtual.id),
      areaClienteService.getExtratoFinanceiroCliente(clienteAtual.id)
    ])
      .then(([osList, contasList]) => {
        if (isMounted) {
          setRecentOS(osList.slice(0, 5));
          setRecentContas(contasList.slice(0, 5));
          setLoadingData(false);
        }
      })
      .catch((err) => {
        console.error('Erro ao carregar dados do dashboard do cliente:', err);
        if (isMounted) setLoadingData(false);
      });

    return () => {
      isMounted = false;
    };
  }, [clienteAtual]);

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(val || 0);
  };

  const getStatusBadge = (statusId?: number) => {
    switch (statusId) {
      case 8:
        return <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">Solicitado / Em Aberto</span>;
      case 1:
        return <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-800 border border-blue-200">Agendado</span>;
      case 2:
        return <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-orange-100 text-orange-800 border border-orange-200">Em Andamento</span>;
      case 4:
        return <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">Finalizado</span>;
      case 7:
        return <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-900 border border-green-300">Medição Gerada</span>;
      case 9:
        return <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-purple-100 text-purple-900 border border-purple-300">Faturado</span>;
      case 3:
        return <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-800 border border-red-200">Cancelado</span>;
      default:
        return <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700">Status #{statusId}</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Welcome Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white rounded-2xl p-6 sm:p-8 shadow-xl border border-slate-700/50 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-gradient-to-l from-red-600/15 to-transparent pointer-events-none" />
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-red-600/30 border border-red-500/40 rounded-full text-xs font-semibold text-red-300">
              <Building2 className="w-3.5 h-3.5" />
              <span>Painel de Atendimento ao Cliente</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Olá, <span className="text-red-400">{clienteAtual?.nome}</span>
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 max-w-2xl leading-relaxed">
              Bem-vindo ao seu portal de autoatendimento. Acompanhe suas ordens de serviço em tempo real,
              consulte medições periódicas, extratos financeiros e solicite novos fretes com facilidade.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/area-cliente/solicitar-frete"
              className="inline-flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white font-bold text-sm px-5 py-3 rounded-xl shadow-lg shadow-red-600/40 transition-all hover:scale-105 active:scale-95"
            >
              <PlusCircle className="w-5 h-5" />
              <span>Solicitar Frete</span>
            </Link>
            <Link
              to="/area-cliente/os"
              className="inline-flex items-center space-x-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-white font-semibold text-sm px-4 py-3 rounded-xl transition-all"
            >
              <span>Ver Minhas OS</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </Link>
          </div>
        </div>
      </div>

      {/* KPI Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Fretes / OS Ativas */}
        <Link
          to="/area-cliente/os"
          className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all group hover:border-red-200"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Ordens de Serviço</span>
            <div className="p-2 bg-red-50 text-red-600 rounded-xl group-hover:bg-red-600 group-hover:text-white transition-colors">
              <Truck className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-3xl font-extrabold text-slate-900">{stats?.totalOS || 0}</span>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
              <span className="font-semibold text-amber-600">{stats?.osEmAberto || 0} em aberto</span>
              <span>•</span>
              <span className="font-semibold text-emerald-600">{stats?.osFinalizadas || 0} finalizadas</span>
            </p>
          </div>
        </Link>

        {/* Medições */}
        <Link
          to="/area-cliente/medicoes"
          className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all group hover:border-blue-200"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Medições de Serviço</span>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition-colors">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-3xl font-extrabold text-slate-900">{stats?.totalMedicoes || 0}</span>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
              <span className="font-semibold text-blue-600">{stats?.medicoesEmAnalise || 0} em análise</span>
              <span>•</span>
              <span>Períodos apurados</span>
            </p>
          </div>
        </Link>

        {/* Extrato Financeiro */}
        <Link
          to="/area-cliente/financeiro"
          className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all group hover:border-emerald-200"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Faturas a Vencer</span>
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl group-hover:bg-emerald-600 group-hover:text-white transition-colors">
              <DollarSign className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl sm:text-3xl font-extrabold text-slate-900 truncate block">
              {formatCurrency(stats?.totalFinanceiroPendente || 0)}
            </span>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
              <span className="text-emerald-600 font-semibold">{formatCurrency(stats?.totalFinanceiroPago || 0)} quitados</span>
            </p>
          </div>
        </Link>

        {/* Propostas e Contratos */}
        <Link
          to="/area-cliente/propostas"
          className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all group hover:border-purple-200"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Contratos & Propostas</span>
            <div className="p-2 bg-purple-50 text-purple-600 rounded-xl group-hover:bg-purple-600 group-hover:text-white transition-colors">
              <FileText className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-3xl font-extrabold text-slate-900">{stats?.totalPropostas || 0}</span>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
              <span className="text-purple-600 font-semibold">Tabelas e tarifas vigentes</span>
            </p>
          </div>
        </Link>
      </div>

      {/* Grid: Recent OS + Recent Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Service Orders (2 cols) */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <div className="flex items-center space-x-2">
              <Truck className="w-5 h-5 text-red-600" />
              <h2 className="text-base font-bold text-slate-800">Últimas Ordens de Serviço</h2>
            </div>
            <Link
              to="/area-cliente/os"
              className="text-xs font-semibold text-red-600 hover:text-red-700 flex items-center gap-1"
            >
              <span>Ver todas ({stats?.totalOS || 0})</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {loadingData ? (
            <div className="py-8 text-center text-xs text-slate-400">Carregando ordens de serviço...</div>
          ) : recentOS.length === 0 ? (
            <div className="py-8 text-center space-y-3">
              <Truck className="w-10 h-10 text-slate-300 mx-auto" />
              <p className="text-xs text-slate-500">Nenhuma ordem de serviço encontrada para sua empresa.</p>
              <Link
                to="/area-cliente/solicitar-frete"
                className="inline-flex items-center space-x-1.5 text-xs font-bold text-red-600 hover:underline"
              >
                <PlusCircle className="w-3.5 h-3.5" />
                <span>Solicite seu primeiro frete agora</span>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="text-slate-400 font-semibold border-b border-slate-100">
                    <th className="py-2.5 pr-2">Nº OS</th>
                    <th className="py-2.5 px-2">Data</th>
                    <th className="py-2.5 px-2">Origem / Destino</th>
                    <th className="py-2.5 px-2">Status</th>
                    <th className="py-2.5 pl-2 text-right">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {recentOS.map((os) => (
                    <tr key={os.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 pr-2 font-bold text-slate-900">{os.id}</td>
                      <td className="py-3 px-2 text-slate-600 whitespace-nowrap">
                        {os.data_atendimento ? new Date(os.data_atendimento + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
                      </td>
                      <td className="py-3 px-2 max-w-[200px] truncate text-slate-700">
                        <div className="truncate font-medium">{os.retirada || 'Sem retirada especificada'}</div>
                        <div className="text-[10px] text-slate-400 truncate">➡️ {os.entrega || 'Sem entrega'}</div>
                      </td>
                      <td className="py-3 px-2 whitespace-nowrap">{getStatusBadge(os.status_id)}</td>
                      <td className="py-3 pl-2 text-right">
                        <Link
                          to="/area-cliente/os"
                          className="text-xs font-semibold text-slate-700 hover:text-red-600 bg-slate-100 hover:bg-red-50 px-2.5 py-1 rounded-md transition-colors"
                        >
                          Ver
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Financial / Invoices Summary (1 col) */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
              <div className="flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                <h2 className="text-base font-bold text-slate-800">Extrato Financeiro</h2>
              </div>
              <Link
                to="/area-cliente/financeiro"
                className="text-xs font-semibold text-red-600 hover:text-red-700 flex items-center gap-1"
              >
                <span>Ver tudo</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {loadingData ? (
              <div className="py-8 text-center text-xs text-slate-400">Carregando extrato...</div>
            ) : recentContas.length === 0 ? (
              <div className="py-8 text-center space-y-2">
                <DollarSign className="w-8 h-8 text-slate-300 mx-auto" />
                <p className="text-xs text-slate-500">Nenhum lançamento financeiro pendente.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentContas.map((conta) => {
                  const isPaid = !!conta.dt_pagamento;
                  const isOverdue = !isPaid && conta.dt_vencimento && conta.dt_vencimento < new Date().toISOString().split('T')[0];

                  return (
                    <div
                      key={conta.id}
                      className="p-3 rounded-xl border border-slate-100 bg-slate-50/60 hover:bg-slate-50 flex items-center justify-between text-xs transition-colors"
                    >
                      <div className="truncate pr-2">
                        <p className="font-bold text-slate-800 truncate">{conta.descricao || 'Fatura de Transporte'}</p>
                        <p className="text-[10px] text-slate-500">
                          Vencimento: {conta.dt_vencimento ? new Date(conta.dt_vencimento + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-extrabold text-slate-900">{formatCurrency(conta.valor)}</p>
                        {isPaid ? (
                          <span className="text-[10px] font-bold text-emerald-600">Quitado</span>
                        ) : isOverdue ? (
                          <span className="text-[10px] font-bold text-red-600">Vencido</span>
                        ) : (
                          <span className="text-[10px] font-bold text-amber-600">Em aberto</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Support Info */}
          <div className="mt-4 pt-4 border-t border-slate-100 bg-red-50/50 rounded-xl p-3.5 border border-red-100">
            <div className="flex items-center gap-2 text-red-900 font-bold text-xs">
              <PhoneCall className="w-4 h-4 text-red-600" />
              <span>Dúvidas ou Faturamento?</span>
            </div>
            <p className="text-[11px] text-slate-600 mt-1">
              Fale com nosso setor operacional e comercial pelo WhatsApp ou telefone direto.
            </p>
            <div className="mt-2 text-xs font-semibold text-red-700">
              (11) 98765-4321 • comercial@voopetrans.com.br
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
